import { BuilderComponent } from '@builder.io/react';
import { NextSeo } from 'next-seo';
import { FC, useCallback, useEffect } from 'react';

import { EventBus } from '@catawiki/runtime-toolkit';

import { getBuilderString } from '@/shared/text';
import { constructUrl } from '@/shared/utils/http';

import config from '../../../config';
import {
  USER_LOGIN,
  USER_LOGIN_PATH,
  USER_REGISTER,
  USER_REGISTER_PATH,
  pathWithRedirectAfterHere,
} from '../constants/appRoutes';
import { isInMobileApp } from '../utils/isInMobileApp';
import { Link } from './Link';
import { UserProvider } from './providers/UserProvider';

const DEFAULT_ROBOTS_CONTENT = 'noindex';

interface Props {
  title: string;
  description?: string;
  locale: string;
  asPath: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  type: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  robots: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  page: any;
  thumbnail?: string;
  modelName: string;
}

type ListOfElements = NodeListOf<Element>;

export const SharedPage: FC<Props> = ({
  title,
  description,
  locale,
  asPath,
  type,
  robots,
  page,
  thumbnail,
  modelName,
}) => {
  const localizedTitle = getBuilderString(title, locale);
  const localizedDescription = getBuilderString(description, locale);
  const inApp = isInMobileApp();

  const processLinksForInapp = useCallback(
    (allSignInAnchors: ListOfElements, allSignUpAnchors: ListOfElements) => {
      const updateUrlsWithRedirectAfter = (
        elements: ListOfElements,
        path: string
      ) => {
        elements.forEach((element) => {
          const anchorElement = element as HTMLAnchorElement;

          anchorElement.href = pathWithRedirectAfterHere(
            path,
            window.location.href
          );
        });
      };

      updateUrlsWithRedirectAfter(allSignInAnchors, USER_LOGIN_PATH);
      updateUrlsWithRedirectAfter(allSignUpAnchors, USER_REGISTER_PATH);
    },
    []
  );

  const processLinksForWeb = useCallback(
    (allSignInAnchors: ListOfElements, allSignUpAnchors: ListOfElements) => {
      const replaceHrefWithEventBusEvent = (
        elements: ListOfElements,
        screen: EventBus.Screen
      ) => {
        elements.forEach((element) => {
          element.addEventListener('click', (e) => {
            e.preventDefault();
            EventBus.showScreen({
              screen,
            });
          });
        });
      };
      const replaceHref = (
        elements: ListOfElements,
        from: string,
        to: string
      ) => {
        elements.forEach((anchor) => {
          if (anchor.getAttribute('href') === from) {
            anchor.setAttribute('href', to);
          }
        });
      };

      replaceHrefWithEventBusEvent(allSignInAnchors, EventBus.Screen.SIGN_IN);
      replaceHrefWithEventBusEvent(allSignUpAnchors, EventBus.Screen.SIGN_UP);
      replaceHref(allSignInAnchors, USER_LOGIN, USER_LOGIN_PATH);
      replaceHref(allSignUpAnchors, USER_REGISTER, USER_REGISTER_PATH);
    },
    []
  );

  useEffect(() => {
    const allSignInAnchors = document.body.querySelectorAll(
      `[href="${USER_LOGIN}"`
    );
    const allSignUpAnchors = document.body.querySelectorAll(
      `[href="${USER_REGISTER}"`
    );

    if (inApp) {
      processLinksForInapp(allSignInAnchors, allSignUpAnchors);
    } else {
      processLinksForWeb(allSignInAnchors, allSignUpAnchors);
    }
  }, [inApp, processLinksForWeb, processLinksForInapp]);

  return (
    <UserProvider>
      <NextSeo
        title={localizedTitle}
        description={localizedDescription}
        canonical={constructUrl(locale, asPath)}
        openGraph={{
          title: localizedTitle,
          description: localizedDescription,
          url: constructUrl(locale, asPath),
          ...(type
            ? {
                type,
              }
            : {}),
          ...(thumbnail
            ? {
                images: [
                  {
                    url: thumbnail,
                  },
                ],
              }
            : {}),
        }}
        languageAlternates={config.acceptedLocales.map((loc) => ({
          hrefLang: loc,
          href: constructUrl(loc, asPath),
        }))}
        robotsProps={robots || DEFAULT_ROBOTS_CONTENT}
      />
      <BuilderComponent
        model={modelName}
        data={{ locale }}
        context={{ locale }}
        locale={locale}
        content={page}
        renderLink={(props) => <Link {...props} />}
      />
    </UserProvider>
  );
};
